/** OAuth 2.0 Authorization Code with PKCE — used for "Continue with Google". */
import { buildSession, env, save } from "./session";

// localStorage (not sessionStorage) because Safari ITP and some
// Firefox modes clear sessionStorage during the cross-origin OAuth
// redirect chain (app → auth → idp → auth → app), which makes the
// callback fail with "missing PKCE verifier".
const VERIFIER_KEY = "tlp.pkce.verifier";
const _vstore = {
  set: (v: string) => { try { localStorage.setItem(VERIFIER_KEY, v); } catch {} },
  get: () => { try { return localStorage.getItem(VERIFIER_KEY); } catch { return null; } },
  clear: () => { try { localStorage.removeItem(VERIFIER_KEY); } catch {} },
};

function randB64(bytes = 32): string {
  const a = new Uint8Array(bytes);
  crypto.getRandomValues(a);
  return base64url(a);
}

function base64url(bytes: ArrayBuffer | Uint8Array): string {
  const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  u8.forEach((b) => (s += String.fromCharCode(b)));
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function challenge(verifier: string): Promise<string> {
  const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64url(buf);
}

const APP_HOST = "app.thelivephotos.com";

export async function startSocialSignIn(provider: "Google" | "Facebook") {
  // The OAuth callback is fixed to https://app.thelivephotos.com/auth/callback.
  // If the user is on a different origin (apex, www, localhost preview, …)
  // the PKCE verifier in localStorage wouldn't be readable from the callback,
  // and the flow would fail with "missing PKCE verifier".
  if (typeof window !== "undefined" && window.location.hostname && window.location.hostname !== APP_HOST) {
    window.location.assign(`https://${APP_HOST}/login`);
    return;
  }

  const verifier = randB64(32);
  _vstore.set(verifier);
  const codeChallenge = await challenge(verifier);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: env.clientId,
    redirect_uri: env.redirectUri,
    scope: "openid email profile",
    identity_provider: provider,
    code_challenge_method: "S256",
    code_challenge: codeChallenge,
  });
  window.location.assign(`${env.domain}/oauth2/authorize?${params}`);
}

// Back-compat alias used by existing callers.
export const startGoogleSignIn = () => startSocialSignIn("Google");

/** Called from /auth/callback. Exchanges ?code=... for tokens, persists session, returns email. */
export async function completeOAuthCallback(code: string): Promise<string> {
  const verifier = _vstore.get();
  _vstore.clear();
  if (!verifier) throw new Error("missing PKCE verifier");

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: env.clientId,
    code,
    redirect_uri: env.redirectUri,
    code_verifier: verifier,
  });

  const r = await fetch(`${env.domain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!r.ok) throw new Error(`token exchange failed: ${r.status} ${await r.text()}`);
  const j = await r.json();
  const session = buildSession(j.id_token, j.access_token, j.refresh_token);
  save(session);
  return session.email;
}
